
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

// TypeScript Interfaces
interface ProjectFile {
  id: string;
  name: string;
  url: string;
  size: number;
  uploadedAt: string;
}

interface ProjectEntry {
  id: string;
  timestamp: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'offer' | 'contract';
  description: string;
  contactPerson: string;
  isAction?: boolean;
  actionCompleted?: boolean;
}

interface Project {
  id: string;
  title: string;
  contactPerson: string;
  category: string;
  status: 'aktiv' | 'wartend' | 'offen' | 'abgeschlossen' | 'storniert';
  created_at: string;
  entries: ProjectEntry[];
  files: ProjectFile[];
}

// Konstanten
const entryTypes = [
  { value: 'email', label: 'E-Mail', icon: 'ri-mail-line' },
  { value: 'call', label: 'Anruf', icon: 'ri-phone-line' },
  { value: 'meeting', label: 'Meeting', icon: 'ri-user-line' },
  { value: 'note', label: 'Notiz', icon: 'ri-file-text-line' },
  { value: 'offer', label: 'Offerte', icon: 'ri-file-list-line' },
  { value: 'contract', label: 'Vertrag', icon: 'ri-file-paper-line' }
];

const projectTypes = [
  'Kundenanfrage',
  'Offerte',
  'Projektentwicklung',
  'Support',
  'Wartung',
  'Beratung',
  'Schulung',
  'Sonstiges'
];

export default function Projekte() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    contactPerson: '',
    category: ''
  });

  const [entryFormData, setEntryFormData] = useState({
    type: 'email' as const,
    description: '',
    timestamp: new Date().toISOString().slice(0, 16),
    contactPerson: '',
    isAction: false
  });

  // Load projects from Supabase Storage
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProjects = async () => {
    try {
      const { data: files, error } = await supabase.storage
        .from('Projekt')
        .list(`${user?.id}/projects`, {
          limit: 100,
          offset: 0
        });

      if (error) {
        console.error('Error listing projects:', error);
        setLoading(false);
        return;
      }

      const projectData: Project[] = [];

      for (const file of files || []) {
        if (file.name.endsWith('.json')) {
          try {
            const { data: fileData, error: downloadError } = await supabase.storage
              .from('Projekt')
              .download(`${user?.id}/projects/${file.name}`);

            if (!downloadError && fileData) {
              const text = await fileData.text();
              const project = JSON.parse(text);
              
              // Convert old pending actions to entries
              if (project.pendingActions && project.pendingActions.length > 0) {
                for (const action of project.pendingActions) {
                  const actionEntry: ProjectEntry = {
                    id: action.id,
                    timestamp: action.createdAt,
                    type: 'note',
                    description: action.description,
                    contactPerson: project.contactPerson,
                    isAction: true,
                    actionCompleted: action.completed
                  };
                  project.entries.push(actionEntry);
                }
                delete project.pendingActions;
              }
              
              projectData.push(project);
            }
          } catch (parseError) {
            console.error('Error parsing project file:', parseError);
          }
        }
      }

      projectData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setProjects(projectData);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save project to storage
  const saveProjectToStorage = async (project: Project) => {
    const fileName = `project_${project.id}.json`;
    const filePath = `${user?.id}/projects/${fileName}`;
    
    const { error } = await supabase.storage
      .from('Projekt')
      .upload(filePath, JSON.stringify(project, null, 2), {
        contentType: 'application/json',
        upsert: true
      });

    if (error) {
      throw error;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEntryInputChange = (field: string, value: string | boolean) => {
    setEntryFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const projectId = Date.now().toString();
    const projectData: Project = {
      id: projectId,
      title: formData.title,
      contactPerson: formData.contactPerson,
      category: formData.category,
      status: 'aktiv',
      created_at: new Date().toISOString(),
      entries: [],
      files: []
    };

    try {
      await saveProjectToStorage(projectData);
      setProjects(prev => [projectData, ...prev]);
      setIsModalOpen(false);
      setFormData({
        title: '',
        contactPerson: '',
        category: ''
      });
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Fehler beim Speichern des Projekts. Bitte versuchen Sie es erneut.');
    }
  };

  // Modified handleAddEntry: every new entry is a mandatory action
  const handleAddEntry = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProject) return;

    const project = projects.find(p => p.id === selectedProject);
    if (!project) return;

    try {
      const newEntry: ProjectEntry = {
        id: Date.now().toString(),
        timestamp: entryFormData.timestamp,
        type: entryFormData.type,
        description: entryFormData.description,
        contactPerson: entryFormData.contactPerson || project.contactPerson,
        isAction: true, // GEÄNDERT: Jeder neue Eintrag ist standardmäßig eine Handlung
        actionCompleted: false // GEÄNDERT: Standardmäßig nicht erledigt
      };

      const updatedEntries = [...project.entries, newEntry];
      const updatedProject = {
        ...project,
        entries: updatedEntries
      };

      await saveProjectToStorage(updatedProject);

      setProjects(prev =>
        prev.map(p => (p.id === selectedProject ? updatedProject : p))
      );

      setIsEntryModalOpen(false);
      setSelectedProject(null);
      setEntryFormData({
        type: 'email',
        description: '',
        timestamp: new Date().toISOString().slice(0, 16),
        contactPerson: '',
        isAction: false
      });
    } catch (error) {
      console.error('Error adding entry:', error);
    }
  };

  // Toggle action completion
  const handleToggleAction = async (projectId: string, entryId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    try {
      const updatedEntries = project.entries.map(entry =>
        entry.id === entryId && entry.isAction
          ? { ...entry, actionCompleted: !entry.actionCompleted }
          : entry
      );

      const updatedProject = {
        ...project,
        entries: updatedEntries
      };

      await saveProjectToStorage(updatedProject);

      setProjects(prev =>
        prev.map(p => (p.id === projectId ? updatedProject : p))
      );
    } catch (error) {
      console.error('Error toggling action:', error);
    }
  };

  // NEU: Einzelnen Eintrag löschen
  const handleDeleteEntry = async (projectId: string, entryId: string) => {
    if (!confirm('Diesen Eintrag wirklich löschen?')) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    try {
      const updatedEntries = project.entries.filter(entry => entry.id !== entryId);
      
      const updatedProject = {
        ...project,
        entries: updatedEntries
      };

      await saveProjectToStorage(updatedProject);

      setProjects(prev =>
        prev.map(p => (p.id === projectId ? updatedProject : p))
      );
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Fehler beim Löschen des Eintrags. Bitte versuchen Sie es erneut.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Projekt wirklich löschen?')) return;

    try {
      const fileName = `project_${id}.json`;
      const filePath = `${user?.id}/projects/${fileName}`;
      
      const { error } = await supabase.storage
        .from('Projekt')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting project file:', error);
      }

      setProjects(prev => prev.filter(project => project.id !== id));
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleEdit = (project: Project) => {
    setFormData({
      title: project.title,
      contactPerson: project.contactPerson,
      category: project.category
    });
    setSelectedProject(project.id);
    setIsModalOpen(true);
    setActiveDropdown(null);
  };

  const toggleDropdown = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === projectId ? null : projectId);
  };

  const getEntryTypeInfo = (type: string) => {
    return entryTypes.find(t => t.value === type) || entryTypes[0];
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('de-DE'),
      time: date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Projekte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 overflow-x-hidden">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
        {/* Mobile-optimized Header */}
        <div className="bg-white rounded-lg shadow-sm border mb-4 sm:mb-6">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <div className="w-8 h-8 sm:hidden bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-folder-line text-blue-600"></i>
                  </div>
                  Projekte
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">
                  Verwalten Sie Ihre Projekte und Handlungen ({projects.length} Projekte)
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center text-sm sm:text-base"
              >
                <i className="ri-add-line mr-2"></i>
                <span className="hidden sm:inline">Neues Projekt</span>
                <span className="sm:hidden">Projekt erstellen</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile-optimized Projects List */}
        {projects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="text-center py-12 px-4">
              <div className="text-gray-400 mb-4">
                <i className="ri-folder-line text-6xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Keine Projekte vorhanden
              </h3>
              <p className="text-gray-500 mb-6 text-sm sm:text-base">
                Erstellen Sie Ihr erstes Projekt, um zu beginnen.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <i className="ri-add-line mr-2"></i>
                Erstes Projekt erstellen
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Mobile-optimized Project Header */}
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 sm:mb-1">{project.title}</h3>
                      <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
                        <p className="text-sm text-gray-600 flex items-center">
                          <i className="ri-user-line mr-1"></i>
                          {project.contactPerson}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center">
                          <i className="ri-folder-line mr-1"></i>
                          {project.category}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center">
                          <i className="ri-calendar-line mr-1"></i>
                          {formatDateTime(project.created_at).date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end space-x-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        project.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                        project.status === 'abgeschlossen' ? 'bg-blue-100 text-blue-800' :
                        project.status === 'wartend' ? 'bg-yellow-100 text-yellow-800' :
                        project.status === 'offen' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {project.status === 'aktiv' ? 'Aktiv' :
                         project.status === 'abgeschlossen' ? 'Abgeschlossen' :
                         project.status === 'wartend' ? 'Wartend' :
                         project.status === 'offen' ? 'Offen' : 'Storniert'}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedProject(project.id);
                            setEntryFormData({
                              type: 'email',
                              description: '',
                              timestamp: new Date().toISOString().slice(0, 16),
                              contactPerson: '',
                              isAction: false
                            });
                            setIsEntryModalOpen(true);
                          }}
                          className="bg-blue-600 text-white px-3 py-1.5 sm:py-1 rounded-md hover:bg-blue-700 transition-colors cursor-pointer text-sm whitespace-nowrap"
                        >
                          <i className="ri-add-line mr-1"></i>
                          <span className="hidden sm:inline">Eintrag</span>
                          <span className="sm:hidden">+</span>
                        </button>
                        <div className="relative dropdown-container">
                          <button
                            onClick={(e) => toggleDropdown(e, project.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 cursor-pointer"
                          >
                            <i className="ri-more-line"></i>
                          </button>
                          {activeDropdown === project.id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border z-50">
                              <div className="py-1">
                                <button
                                  onClick={() => handleEdit(project)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <i className="ri-edit-line mr-2"></i>
                                  Bearbeiten
                                </button>
                                <button
                                  onClick={() => handleDelete(project.id)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line mr-2"></i>
                                  Löschen
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile-optimized Project Entries */}
                <div className="p-4 sm:p-6">
                  {project.entries.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <i className="ri-file-list-line text-3xl"></i>
                      </div>
                      <p className="text-gray-500 text-sm">Noch keine Einträge vorhanden</p>
                      <button
                        onClick={() => {
                          setSelectedProject(project.id);
                          setEntryFormData({
                            type: 'email',
                            description: '',
                            timestamp: new Date().toISOString().slice(0, 16),
                            contactPerson: '',
                            isAction: false
                          });
                          setIsEntryModalOpen(true);
                        }}
                        className="mt-3 text-blue-600 hover:text-blue-700 cursor-pointer text-sm"
                      >
                        Ersten Eintrag hinzufügen
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {project.entries
                        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                        .map((entry) => {
                          const typeInfo = getEntryTypeInfo(entry.type);
                          const dateTime = formatDateTime(entry.timestamp);
                          
                          return (
                            <div 
                              key={entry.id} 
                              className={`p-4 rounded-lg border-l-4 transition-colors ${
                                entry.isAction && entry.actionCompleted 
                                  ? 'bg-red-50 border-red-400' 
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-3 sm:space-y-0">
                                <div className="flex-1">
                                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mb-2">
                                    <div className="flex items-center space-x-2">
                                      <i className={`${typeInfo.icon} ${
                                        entry.isAction && entry.actionCompleted ? 'text-red-600' : 'text-gray-600'
                                      }`}></i>
                                      <span className={`font-medium ${
                                        entry.isAction && entry.actionCompleted ? 'text-red-800' : 'text-gray-800'
                                      }`}>
                                        {typeInfo.label}
                                        {entry.isAction && (
                                          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                            Handlung
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    <span className={`text-sm ${
                                      entry.isAction && entry.actionCompleted ? 'text-red-500' : 'text-gray-500'
                                    }`}>
                                      {dateTime.date} um {dateTime.time}
                                    </span>
                                  </div>
                                  <p className={`text-sm sm:text-base ${
                                    entry.isAction && entry.actionCompleted ? 'text-red-700' : 'text-gray-700'
                                  }`}>
                                    {entry.description}
                                  </p>
                                  {entry.contactPerson && entry.contactPerson !== project.contactPerson && (
                                    <p className="text-sm text-gray-500 mt-1">
                                      Kontakt: {entry.contactPerson}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center justify-end space-x-2">
                                  {/* Mobile-optimized Delete Button */}
                                  <button
                                    onClick={() => handleDeleteEntry(project.id, entry.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                                    title="Eintrag löschen"
                                  >
                                    <i className="ri-delete-bin-line text-sm"></i>
                                  </button>
                                  {/* Mobile-optimized Toggle Button */}
                                  <button
                                    onClick={() => handleToggleAction(project.id, entry.id)}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                                      entry.actionCompleted ? 'bg-red-400' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                      entry.actionCompleted ? 'translate-x-5' : 'translate-x-1'
                                    }`}></span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile-optimized Modal für neues Projekt */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
          setFormData({
            title: '',
            contactPerson: '',
            category: ''
          });
        }}
        title={selectedProject ? "Projekt bearbeiten" : "Neues Projekt erstellen"}
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Projekttitel"
            value={formData.title}
            onChange={e => handleInputChange('title', e.target.value)}
            placeholder="z.B. Hotel Projekt"
            required
          />
          <Input
            label="Kontaktperson"
            value={formData.contactPerson}
            onChange={e => handleInputChange('contactPerson', e.target.value)}
            placeholder="z.B. Andreas Schatz"
            required
          />
          <Select
            label="Kategorie"
            options={projectTypes.map(type => ({ value: type, label: type }))}
            value={formData.category}
            onChange={value => handleInputChange('category', value)}
            required
          />

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              {selectedProject ? "Projekt aktualisieren" : "Projekt erstellen"}
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              className="flex-1"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedProject(null);
                setFormData({
                  title: '',
                  contactPerson: '',
                  category: ''
                });
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Mobile-optimized Modal für neuen Eintrag */}
      <Modal
        isOpen={isEntryModalOpen}
        onClose={() => {
          setIsEntryModalOpen(false);
          setSelectedProject(null);
        }}
        title="Neuen Eintrag hinzufügen"
        maxWidth="lg"
      >
        <form onSubmit={handleAddEntry} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Typ"
              options={entryTypes.map(type => ({ value: type.value, label: type.label }))}
              value={entryFormData.type}
              onChange={value => handleEntryInputChange('type', value)}
              required
            />
            <Input
              label="Datum & Zeit"
              type="datetime-local"
              value={entryFormData.timestamp}
              onChange={e => handleEntryInputChange('timestamp', e.target.value)}
              required
            />
          </div>

          <Input
            label="Kontaktperson (optional)"
            value={entryFormData.contactPerson}
            onChange={e => handleEntryInputChange('contactPerson', e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
            <textarea
              value={entryFormData.description}
              onChange={e => handleEntryInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Beschreibung des Eintrags..."
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center">
              <i className="ri-toggle-line text-blue-600 mr-2"></i>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Automatischer Toggle-Button</p>
                <p>Jeder neue Eintrag erhält automatisch einen Toggle-Button zum Abhaken.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Eintrag hinzufügen
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setIsEntryModalOpen(false);
                setSelectedProject(null);
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
